import React, { useEffect, Fragment, useState, useRef, useCallback } from 'react';
import { FormattedNumber, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

//Components
import { NotificationManager } from 'Components/Notifications';
import {
    TableView,
    IconButton,
    SearchInput,
    TableButtons,
    Link
} from '@gpstrackit/gtc-react-controls';
import { MenuComponent } from 'Components/Menu';
import { Grid } from '@mui/material';

//Hooks
import { usePaggination } from '../../../../../../core/hooks';

//Actions
import { reset, maintenanceVSTSearchServices } from './redux/slice';
import { openModalCreateEditMaintenanceService } from '../../../../modals/createEditMaintenanceService/redux/slice';
import { openModalEntityDetails, openModalManageServiceTasks, openModalDeleteMaintenanceService, sendGenericExport, clearRedux } from 'Redux/actions';

//Views
import CreateEditMaintenanceService from '../../../../modals/createEditMaintenanceService';
import CompleteMaintenanceService from '../../../../modals/completeMaintenanceService';
import DeleteMaintenanceService from '../../../../modals/deleteMaintenanceService';
import ManageServiceItems from '../../../../modals/manageServiceTasks';
import EntityDetails from '../../../../modals/entityDetails';
import {convertToKPH, speedFormatMPH} from "Core/data/Helpers";

const ServicesTab = (props) => {
    const { messages, locale } = useIntl();
    const tableRef = useRef();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    //Options
    const menuServicesTabOptions = [messages['edit'], messages['delete']];

    //Redux States
    const openCreateEditMaintenanceService = useSelector(state => state.maintenanceCreateEditMaintenanceServiceRedux.open);
    const openCompleteMaintenanceService = useSelector(state => state.maintenanceCompleteMaintenanceServiceRedux.open);
    const openDeleteMaintenanceService = useSelector(state => state.maintenanceDeleteMaintenanceServiceRedux.open);
    const openManageServiceTasks = useSelector(state => state.maintenanceManageServiceTasksRedux.open);
    const { loading, items, total } = useSelector(state => state.maintenanceServicesTabRedux);
    const openEntityDetails = useSelector(state => state.maintenanceEntityDetailsRedux.open);
    const exportResult = useSelector(state => state.commomRedux.exportResult);
    const exportName = useSelector(state => state.commomRedux.exportName);
    const { user } = useSelector((state) => state.securityRedux);
    //Local States
    const [selectedItems, setSelectedItems] = useState([]);

    //Hook
    const paggination = usePaggination(maintenanceVSTSearchServices, ["title"]);

    useEffect(() => {
        paggination.refresh()
        return () => dispatch(reset())
    }, []);

    useEffect(() => {
        paggination.setColumns([
            {
                key: 'title',
                title: messages['scheduleDriverTask_service'],
                show: true,
                export: true,
                sort: true,
                width: 200,
                render: (row) => {
                    return (
                        <Grid container alignItems="center">
                            <Grid item xs={9} style={{ width: 200 }}>
                                <Link
                                    className="table-span"
                                    dest={`/app/maintenance/serviceDetail/${row.id}`}
                                    onClick={() =>
                                        navigate(`/app/maintenance/serviceDetail/${row.id}`)
                                    }
                                    text={row.title}
                                ></Link>
                            </Grid>
                            <Grid item xs={3}>
                                <MenuComponent
                                    disabled={loading}
                                    options={menuServicesTabOptions}
                                    icon="more_vert"
                                    className="showInHover"
                                    onClick={handleClickMenu}
                                    value={row}
                                />
                            </Grid>
                        </Grid>
                    )
                },
                functionString: 'return row?.title'
            },
            {
                key: 'miles',
                title: user.isKph ? messages['scheduleDriverTask_km'] : messages['scheduleDriverTask_miles'],
                show: true,
                export: true,
                sort: true,
                render: (row) => {
                    return (
                        <span>{(row.miles && `${user.isKph ? convertToKPH(row.miles) : row.miles} ${user.isKph ? messages['kilometers'] : messages['miles']}`) || '---'}</span>
                    )
                },
                functionString: `
                    let kph = ${user.isKph}
                    let labelKm = ' ${messages['kilometers']}'
                    let labelMiles = ' ${messages['miles']}'
                    let miles = row?.miles ? row?.miles + (kph ? labelKm : labelMiles) : ""
                    return miles
                `
            },
            {
                key: 'days',
                title: messages['scheduleDriverTask_days'],
                show: true,
                export: true,
                sort: true,
                render: (row) => {
                    return (
                        <span>{(row.days && `${row.days} ${messages['days']}`) || '---'}</span>
                    )
                },
                functionString: `
                    let lag = "${locale}"
                    let days = row?.days ? row?.days + (lag === "en" ? " Days" : " Días") : ""
                    return days
                `
            },
            {
                key: 'engineHours',
                title: messages['scheduleDriverTask_hours'],
                show: true,
                export: true,
                sort: true,
                render: (row) => {
                    return (
                        <span>
                            {(row.engineHours && `${row.engineHours} ${messages['hours']}`) || '---'}
                        </span>
                    )
                },
                functionString: `
                    let lag = "${locale}"
                    let engineHours = row?.engineHours ? row?.engineHours + (lag === "en" ? " Hours" : " Horas") : ""
                    return engineHours
                `
            },
            {
                key: 'estimatedCost',
                title: messages['scheduleDriverTask_cost'],
                show: true,
                export: true,
                sort: true,
                render: (row) => {
                    return (
                        <span>
                            {
                                (row.estimatedCost && (<FormattedNumber value={row.estimatedCost} style="currency" currency="USD" />)) ||
                                '---'
                            }
                        </span>
                    )
                },
                functionString: `
                    const format = { style: 'currency', currency: 'USD' };
                    const numberFormat = new Intl.NumberFormat('en-US', format);
                    return row?.estimatedCost ? numberFormat.format(row.estimatedCost) : '';
                `
            },
            {
                key: 'subscribersCount',
                title: messages['vehicleDetailView_subscribers'],
                show: true,
                export: true,
                sort: true,
                render: (row) => {
                    return (
                        <>
                            {
                                row.subscribersCount > 0 &&
                                <IconButton
                                    placement="bottom"
                                    variant="secondary"
                                    icon="people"
                                    size="small"
                                    disabled={loading}
                                    onClick={() => handleDetails(row.id, 'MaintenanceService', 0, row.title)}
                                />
                            }
                        </>
                    )
                },
                functionString: `return row?.subscribersCount || ""`
            },
            {
                key: 'notes',
                title: messages['scheduleDriverTask_notes'],
                show: true,
                export: true,
                sort: true,
                render: (row) => {
                    return (
                        <>
                            {
                                row.notes &&
                                <IconButton
                                    placement="bottom"
                                    variant="secondary"
                                    icon="comment"
                                    size="small"
                                    disabled={loading}
                                    onClick={() => handleDetails(row.id, 'MaintenanceService', 1, row.title)}
                                />
                            }
                        </>
                    )
                },
                functionString: `return row?.notes`
            },
            {
                key: 'attachments',
                title: messages['vehicleDetailView_attachments'],
                show: true,
                export: true,
                render: (row) => {
                    return (
                        <>
                            {
                                (row.fileIds && row.fileIds.length > 0) &&
                                <IconButton
                                    placement="bottom"
                                    variant="secondary"
                                    icon="attach_file"
                                    size="small"
                                    disabled={loading}
                                    onClick={() => handleDetails(row.id, 'MaintenanceService', 2, row.title)}
                                />
                            }
                        </>
                    )
                },
                functionString: `return row?.fileIds?.length || ""`
            },
        ]);
    }, [locale, user]);

    useEffect(() => {
        if (exportResult && exportName === "services_list") {
            NotificationManager.info(
                messages['exportInProcessBody'],
                messages['exportInProcessTitle'],
                4000,
                null,
                null,
                'info',
                null
            );
            dispatch(clearRedux("COMMON", { exportResult: null, exportName: null }));
        }
    }, [exportResult, exportName]);

    const handleDetails = (entityId, entityName, numberTab, title) => {
        dispatch(openModalEntityDetails({ open: true, entityId, entityName, numberTab, title }))
    };

    const handleClickMenu = (type, maintenanceService) => {
        if (type === messages['edit']) {
            dispatch(openModalCreateEditMaintenanceService(maintenanceService.id))
        }
        if (type === messages['delete']) {
            if (maintenanceService?.completedServices > 0) {
                let label = `${messages["maintenanceDeleteMaintenanceService_theServiceLabel"]} "${maintenanceService?.title}" ${messages["scheduleDriverTask_errorServiceCompleted"]}`;
                NotificationManager.error(
                    label,
                    messages["updateService_errorLabel"],
                    4000,
                    null,
                    null,
                    "error",
                );
            } else {
                dispatch(openModalDeleteMaintenanceService({ open: true, maintenanceService, maintenanceServicesList: [] }))
            }
        }
    };

    const deleteMaintenanceService = () => {

        let servicesCompleted = selectedItems && selectedItems.find((service) => service.completedServices > 0);
        let servicesScheduled = selectedItems && selectedItems.filter((service) => service.scheduledServices >= 0);

        if (servicesCompleted) {
            let label = `${messages["maintenanceDeleteMaintenanceService_theServiceLabel"]} "${servicesCompleted?.title}" ${messages["scheduleDriverTask_errorServiceCompleted"]}`
            NotificationManager.error(
                label,
                messages["updateService_errorLabel"],
                4000,
                null,
                null,
                "error",
            );
        }
        if (!servicesCompleted && servicesScheduled) {
            dispatch(openModalDeleteMaintenanceService({
                open: true,
                maintenanceService: {},
                maintenanceServicesList: servicesScheduled,
            }));
        }
    };

    const handleExport = useCallback((type) => {
        let currentConditions = paggination.currentConditions;
        let currentSort = paggination.currentSort;
        const columns = paggination?.columns
        const headers = columns.filter(x => x.show && x.export).map(x => { return { header: x.title, column: x.key, functionString: x.functionString } }) || [];
        const columnStyles = {6: {cellWidth: 140}}

        if(selectedItems?.length >= 1){
            currentConditions.push({ 
                fields: ["id"], 
                terms: selectedItems.map(item => item.id),
                exact: true
            })
        }

        dispatch(sendGenericExport(
            "services_list",
            type,
            `
                query SearchMaintenanceServices($conditions: [MaintenanceServiceSearchInput], $sort: [MaintenanceServiceSortInput], $limit: Int, $offset: Int) {
                    data: searchMaintenanceServices(conditions: $conditions, sort: $sort, limit: $limit, offset: $offset) {
                        total
                        items {
                            id
                            title
                            notes
                            miles
                            milesAfter
                            milesBefore
                            days
                            daysAfter
                            daysBefore
                            engineHours
                            engineHoursAfter
                            engineHoursBefore
                            recurrent
                            estimatedCost
                            subscribersCount
                            fileIds
                            completedServices
                            scheduledServices
                        }
                    }
                }
            `,
            {
                conditions: currentConditions,
                sort: currentSort,
                limit: 10,
                offset: 0
            },
            headers,
            { unit: 'px', size: 'A4', orientation: 'landscape', fontSize: 10 },
            null,
            null,
            true,
            columnStyles
        ));
    }, [paggination.currentConditions, paggination.currentSort, selectedItems]);

    const refreshTable = () => {
        setSelectedItems([])
        tableRef?.current?.setSelectedItems([]);
        paggination.refresh();
    }

    return (
        <Fragment>
            <Grid container direction="row" justifyContent="space-between" alignItems="center">
                <Grid item xs={6} sm={2}>
                    <Grid
                        container
                        direction="row"
                        justifyContent="flex-start"
                        alignItems="center"
                        spacing={1}
                    >
                        <Grid item style={{ display: 'flex' }}>
                            <IconButton
                                tooltip={messages["add"]}
                                placement="bottom"
                                variant="secondary"
                                icon="add_box"
                                size="small"
                                disabled={loading}
                                onClick={() => dispatch(openModalCreateEditMaintenanceService())}
                            />
                            <IconButton
                                tooltip={messages["scheduleDriverTask_manage_service_items"]}
                                placement="bottom"
                                variant="secondary"
                                icon="assignment"
                                size="small"
                                disabled={loading}
                                onClick={() => dispatch(openModalManageServiceTasks({ open: true }))}
                            />
                            <IconButton
                                tooltip={messages["scheduleVehicleService_delete_button_bar"]}
                                placement="bottom"
                                variant="secondary"
                                icon="delete"
                                size="small"
                                onClick={() => deleteMaintenanceService()}
                                style={{ opacity: !selectedItems.length && '0.6' }}
                                isDisabled={!selectedItems.length}
                            />
                            <TableButtons
                                tableRef={tableRef}
                                buttons={["export"]}
                                onExport={handleExport}
                            />
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item xs={12} sm={4} className="mb-10 text-right">
                    <SearchInput
                        placeholder={messages['search']}
                        searchText={paggination.searchText}
                        onSearchText={paggination.onSearchChange}
                    />
                </Grid>
            </Grid>
            <TableView
                ref={tableRef}
                keyColumn="id"
                data={items || []}
                total={total || 0}
                loading={loading}
                hideAllInPagination
                topheight={250}
                hideDragable
                onSelectedItems={(items) => setSelectedItems(items)}
                {...paggination}
            />
            {openDeleteMaintenanceService && <DeleteMaintenanceService refresh={() => refreshTable()} />}
            {openCreateEditMaintenanceService && <CreateEditMaintenanceService refresh={() => refreshTable()} />}
            {openCompleteMaintenanceService && <CompleteMaintenanceService />}
            {openManageServiceTasks && <ManageServiceItems />}
            {openEntityDetails && <EntityDetails refresh={() => refreshTable()} />}
        </Fragment>
    );
};

export default ServicesTab;
